import type { CSSProperties } from 'react'
import { useMemo, useRef } from 'react'
import { arrow, autoUpdate, flip, hide, offset, shift, useFloating, type OffsetOptions, type Placement, type ReferenceType } from '@floating-ui/react-dom'
import { getFloatingPositionStyle } from 'LuxKit/utils/floatingPositionStyle'
import type { TextDirection } from 'lib/dom/logicalScroll'

const VIEWPORT_PADDING = 8

/** Which side of its anchor a prompt sits on */
export type PromptSide = 'top' | 'bottom'

export interface PromptPositionOptions {
  placement: Placement
  offset: OffsetOptions
  /** Keeps the tail this far from the prompt's rounded corners */
  arrowPadding?: number
  /** Whether the prompt may move to the anchor's other side when there's no room on its own */
  canFlip?: boolean
}

function getArrowStyle(x: number | undefined, textDirection: TextDirection): CSSProperties {
  if (x === undefined) return {}
  // floating-ui measures from the physical left edge.
  return textDirection === 'rtl' ? { insetInlineEnd: x } : { insetInlineStart: x }
}

/**
 * Pins a prompt to an anchor element, and hides it while the anchor is scrolled out of view. Fixed
 * positioning keeps it clear of the landing page's clipped containers while staying in the anchor's DOM
 * order. `boundary` keeps the prompt sliding sideways within an element rather than the whole viewport.
 */
export function usePromptPosition(
  anchorElement: ReferenceType | null,
  textDirection: TextDirection,
  options: PromptPositionOptions,
  boundary?: Element,
) {
  const arrowRef = useRef<HTMLSpanElement>(null)
  const { placement: preferredPlacement, offset: offsetOptions, arrowPadding, canFlip = true } = options
  const middleware = useMemo(() => [
    offset(offsetOptions),
    canFlip && flip({ padding: VIEWPORT_PADDING }),
    shift({ padding: VIEWPORT_PADDING, boundary }),
    arrow({ element: arrowRef, padding: arrowPadding }),
    hide(),
  ], [arrowPadding, boundary, canFlip, offsetOptions])

  const { x, y, strategy, placement, refs, elements, middlewareData, isPositioned } = useFloating<ReferenceType>({
    placement: preferredPlacement,
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    elements: { reference: anchorElement },
    middleware,
  })

  const floatingStyle = useMemo(
    () => getFloatingPositionStyle(strategy, x, y, textDirection),
    [strategy, textDirection, x, y],
  )
  const arrowX = middlewareData.arrow?.x
  const arrowStyle = useMemo(() => getArrowStyle(arrowX, textDirection), [arrowX, textDirection])
  const side: PromptSide = placement.startsWith('top') ? 'top' : 'bottom'

  return {
    setFloating: refs.setFloating,
    floatingElement: elements.floating,
    floatingStyle,
    arrowRef,
    arrowStyle,
    side,
    isVisible: isPositioned && !middlewareData.hide?.referenceHidden,
  }
}
